<template>
  <div class="pathway-detail-page">
    <!-- Hero Section -->
    <section class="hero-section position-relative d-flex flex-column justify-end" :style="heroBackgroundStyle">
      <div class="hero-overlay w-100 h-100 d-flex flex-column justify-end">
        <v-container class="px-4 px-lg-0">
          <div class="hero-content">
            <h1 class="hero-title text-white font-weight-bold mb-4">{{ heroTitleLine1 }}<br />{{ heroTitleLine2 }}</h1>
            <v-btn size="large" class="hero-btn" @click="scrollToDegrees"> View degree pathways </v-btn>
          </div>
        </v-container>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <v-container class="pa-0">
        <div class="cta-card">
          <v-row no-gutters>
            <v-col cols="12" lg="9" class="cta-text-column pe-lg-12">
              <h2 class="cta-title font-weight-bold mb-6 mt-0">
                {{ pathway.ctaHeadline }}
              </h2>
              <p class="body-text mb-0">
                {{ pathway.ctaDescription }}
              </p>
            </v-col>
            <v-col cols="12" lg="3" class="cta-facts-column">
              <h2 class="cta-title font-weight-bold mb-6 mt-0">Quick facts</h2>
              <div class="quick-fact">
                <p class="quick-fact-label">Credits</p>
                <p class="quick-fact-value">{{ creditsNeeded }}</p>
                <div class="quick-fact-detail">
                  {{ pathway.requirements.creditsOver22 }} credits if learner is 22 years old or older,<br />
                  {{ pathway.requirements.creditsUnder22 }} credits if learner is under 22 years old
                </div>
              </div>
              <div class="quick-fact">
                <p class="quick-fact-label">Minimum GPA</p>
                <p class="quick-fact-value">{{ pathway.requirements.gpa }}</p>
                <div class="quick-fact-detail-empty" />
              </div>
            </v-col>
          </v-row>
          <hr class="cta-divider" />
          <div class="cta-note">
            <strong>Please note:</strong> Degrees can have specific or additional requirements. We encourage you to
            become familiar with the requirements of your degree of interest. Browse degrees below for more information.
          </div>
        </div>
      </v-container>
    </section>

    <!-- Degree Pathways Section -->
    <section id="section_degrees" class="degrees-section">
      <v-container class="pa-0">
        <v-row no-gutters>
          <!-- Left Column - Content -->
          <v-col cols="12" lg="8" class="degrees-content-column pr-lg-12">
            <h2 class="degrees-title font-weight-bold mt-0">Degree pathways</h2>
            <p class="degrees-subtitle">
              Select a degree name to learn more and <span class="highlight-register">register</span>
            </p>

            <!-- Search and Filter Row -->
            <div class="search-filter-row">
              <!-- Search Box -->
              <div class="degrees-search-wrapper">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="degrees-search-input"
                  placeholder="Search by degree title"
                />
                <svg
                  class="degrees-search-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>

              <!-- Learning Experience Filter (Engineering only) -->
              <div v-if="interest === 'engineering'" class="learning-experience-filter">
                <v-menu v-model="filterMenuOpen" offset-y>
                  <template #activator="{ props: menuProps }">
                    <button
                      v-bind="menuProps"
                      class="filter-button"
                      :class="{ 'filter-button-active': filterMenuOpen }"
                    >
                      <span class="filter-label">Learning experiences</span>
                      <svg
                        class="filter-icon"
                        :class="{ 'filter-icon-open': filterMenuOpen }"
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  </template>
                  <v-list class="filter-menu">
                    <v-list-item
                      v-for="option in learningExperienceOptions"
                      :key="option.value"
                      @click="selectedLearningExperience = option.value"
                    >
                      <v-list-item-title class="d-flex align-center justify-space-between">
                        {{ option.label }}
                        <svg
                          v-if="selectedLearningExperience === option.value"
                          class="check-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Degree List -->
            <div class="degrees-list-container">
              <!-- Loading State -->
              <div v-if="catalogLoading || !catalogReady" class="degrees-loading">
                <p>Loading pathways...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="catalogError" class="degrees-error">
                <p>Unable to load pathways. Please try again later.</p>
              </div>

              <!-- Empty State -->
              <div v-else-if="displayedDegrees.length === 0" class="degrees-empty">
                <p>No pathways found for this interest area.</p>
              </div>

              <!-- Degree List -->
              <template v-else>
                <div class="degrees-list">
                  <div
                    v-for="degree in displayedDegrees"
                    :key="degree.id"
                    class="degree-item"
                    @click="handleViewPathwayDetails(degree)"
                  >
                    <p class="degree-item-title">{{ degree.name }}</p>
                    <svg
                      class="degree-item-arrow"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </div>
                </div>
                <a
                  v-if="filteredDegrees.length > initialDisplayCount && !showAllDegrees"
                  href="javascript:void(0)"
                  class="view-all-link"
                  @click="showAllDegrees = true"
                >
                  View all
                </a>
              </template>
            </div>
          </v-col>

          <!-- Right Column - Image -->
          <v-col cols="12" lg="4" class="d-none d-lg-block">
            <img :src="pathway.sidebarImageUrl" alt="Student in degree pathway" class="degrees-image" />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Pathway Details Sidebar -->
    <pathway-details-sidebar
      v-model="showSidebar"
      :pathway-id="selectedPathway?.pathwayId"
      :pathway-data="pathwayDataForSidebar"
      @pathway-enrolled="handlePathwayEnrolled"
      @enroll-pathway="handleEnrollFromSidebar"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import pathwaysData from '@/data/pathways.json';
import { usePathwayCatalog } from '@/composables/usePathwayCatalog';
import { useUserStore } from '@/stores/user';
import { SessionInitializer } from '@/mixins/sessionInitializer';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';
import PathwayDetailsSidebar from '@/components/pathways/PathwayDetailsSidebar.vue';

// Create a maroon arrow icon inline (we'll use a simple SVG)
const arrowRightMaroonIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238c1d40' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12h14M12 5l7 7-7 7'/%3E%3C/svg%3E";

export default defineComponent({
  name: 'PathwayDetailPage',

  components: {
    PathwayDetailsSidebar
  },

  mixins: [SessionInitializer, DataLayerMixin],

  props: {
    interest: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      learnerBirthDate: null
    };
  },

  created() {
    const userStore = useUserStore();

    // Check if data already exists (user already logged in from another page)
    const existingBirthDate = this.$conn?.learnerData?._birthDate || userStore.apis?.learnerData?._birthDate;
    if (existingBirthDate) {
      this.learnerBirthDate = existingBirthDate;
    }

    // Try to initialize session to get learner data (non-blocking for non-logged in users)
    this.init('pathwayDetail', () => {
      // Session initialized, update if we got new data
      const birthDate = this.$conn?.learnerData?._birthDate || userStore.apis?.learnerData?._birthDate;
      if (birthDate) {
        this.learnerBirthDate = birthDate;
      }

      // Check partner access after session is initialized
      this.checkAndRedirectIfUnauthorized();
    });
  },

  methods: {
    checkAndRedirectIfUnauthorized() {
      const isPathwaysAllowed = this.$conn?.learnerData?._isPathwaysAllowed;

      // If pathways is not allowed for this user, redirect to dashboard
      if (isPathwaysAllowed === false) {
        this.$router.replace({ name: 'dashboard' });
        return;
      }

      // Access allowed - load the partner's catalog now that the session knows the partner
      // Note: Backend filters pathways by partner, so unauthorized categories will show empty list
      this.loadCatalog();
    }
  },

  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const instance = getCurrentInstance();

    // Use the pathway catalog composable
    const {
      pathways: allPathways,
      loading: catalogLoading,
      error: catalogError,
      fetchCatalog,
      getPathwaysByInterestArea,
      searchPathways: searchCatalogPathways
    } = usePathwayCatalog();

    // Get pathway category data based on route param (for page metadata like title, description)
    const pathway = computed(() => {
      const interest = props.interest || route.params.interest;
      const found = pathwaysData.categories.find((cat) => cat.id === interest);
      return found || pathwaysData.categories[0]; // Default to first if not found
    });

    // Format subheadline with school link
    const formattedSubheadline = computed(() => {
      const p = pathway.value;
      if (!p.schoolName || !p.schoolLink) {
        return p.subheadline;
      }
      // Replace school name in subheadline with linked version
      return p.subheadline.replace(
        p.schoolName,
        `<a href="${p.schoolLink}" target="_blank" class="school-link">${p.schoolName}</a>`
      );
    });

    // Split school name for hero title
    const heroTitleLine1 = computed(() => {
      const schoolName = pathway.value?.schoolName || '';
      const categoryTitle = pathway.value?.title || '';

      if (!schoolName || !categoryTitle) {
        return schoolName;
      }

      // Remove the category word from the end of school name if it exists
      // For example: "W. P. Carey School of Business" -> "W. P. Carey School of"
      if (schoolName.endsWith(categoryTitle)) {
        return schoolName.substring(0, schoolName.length - categoryTitle.length).trim();
      }
      return schoolName;
    });

    const heroTitleLine2 = computed(() => {
      const title = pathway.value?.title || '';
      return title ? `${title} pathways` : 'pathways';
    });

    // Hero background style - use pathway heroImage or placeholder
    const heroBackgroundStyle = computed(() => {
      const heroImage = pathway.value.heroImage;
      if (heroImage) {
        return { backgroundImage: `url('${heroImage}')` };
      }
      // Placeholder gradient when no image is provided
      return { background: 'linear-gradient(135deg, #5C6670 0%, #2E3338 100%)' };
    });

    // The catalog is filtered by partner, so it is only fetched once the learner's partner is known:
    // right away when arriving from another page, otherwise after session init (direct link or refresh).
    // Fetching earlier returned the unfiltered all-partner catalog.
    const catalogReady = ref(false);
    const loadCatalog = async () => {
      try {
        await fetchCatalog();
      } catch {
        // The composable records the failure in catalogError, which the template shows
      } finally {
        catalogReady.value = true;
      }
    };

    onMounted(() => {
      if (instance?.proxy?.$conn?.learnerData?.partnerId) {
        loadCatalog();
      }
    });

    // Get pathways for the current interest area from API
    const degrees = computed(() => {
      // Use apiInterestArea for exact matching with API data
      const interestArea = pathway.value?.apiInterestArea;
      if (!interestArea || !allPathways.value.length) return [];

      // Filter pathways by exact interest area match
      const filtered = allPathways.value.filter((p) => p.interestArea && p.interestArea === interestArea);

      return filtered.map((p) => ({
        id: p.pathwayId,
        name: p.pathwayName,
        link: `#`, // Will be used to open detail/registration
        pathwayData: p // Keep full data for potential use
      }));
    });

    // Search and display state
    const searchQuery = ref('');
    const showAllDegrees = ref(false);
    const initialDisplayCount = 8;

    // Learning experience filter
    const selectedLearningExperience = ref('all');
    const filterMenuOpen = ref(false);
    const learningExperienceOptions = [
      { label: 'All', value: 'all' },
      { label: 'In-person', value: 'in-person' },
      { label: 'Online', value: 'online' }
    ];

    // Sidebar state
    const showSidebar = ref(false);
    const selectedPathway = ref(null);

    // Open sidebar with pathway details
    const handleViewPathwayDetails = (degree) => {
      // GA tracking for degree row click with dynamic text
      if (instance?.proxy?.asuoDataLayerClick) {
        instance.proxy.asuoDataLayerClick('PathwayDetailPage', '0', { text: degree.name });
      }
      selectedPathway.value = degree.pathwayData;
      showSidebar.value = true;
    };

    // Transform pathway data for the sidebar component
    const pathwayDataForSidebar = computed(() => {
      const p = selectedPathway.value;
      if (!p) return {};

      return {
        pathwayId: p.pathwayId,
        pathwayName: p.pathwayName,
        pathwayDescription: p.pathwayDescription,
        imageUrl: p.imageUrl,
        location: p.location || 'Online',
        credits: p.credits,
        creditsNeeded: p.creditsNeeded || p.credits,
        minGPA: p.minGPA,
        admissionRequirements: p.admissionRequirements,
        courses: p.courses || { required: [], recommended: [] }
      };
    });

    // Handle pathway enrollment from sidebar
    const handlePathwayEnrolled = () => {
      showSidebar.value = false;
    };

    const handleEnrollFromSidebar = () => {
      if (selectedPathway.value?.pathwayId) {
        sessionStorage.setItem('pendingPathway', selectedPathway.value.pathwayId);
        showSidebar.value = false;
        router.push({ name: 'dashboard' });
      }
    };

    const filteredDegrees = computed(() => {
      let filtered = degrees.value;

      // Filter by search query
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter((d) => d.name.toLowerCase().includes(query));
      }

      // Filter by learning experience
      if (selectedLearningExperience.value !== 'all') {
        filtered = filtered.filter((d) => {
          const location = d.pathwayData?.location || '';

          if (selectedLearningExperience.value === 'online') {
            // Match "Online" or "Online, On Campus"
            return location.includes('Online');
          } else if (selectedLearningExperience.value === 'in-person') {
            // Match "On Campus" or "Online, On Campus"
            return location.includes('On Campus');
          }

          return true;
        });
      }

      return filtered;
    });

    const displayedDegrees = computed(() => {
      if (showAllDegrees.value || searchQuery.value) {
        return filteredDegrees.value;
      }
      return filteredDegrees.value.slice(0, initialDisplayCount);
    });

    const scrollToDegrees = () => {
      const element = document.getElementById('section_degrees');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    };

    return {
      pathway,
      formattedSubheadline,
      heroBackgroundStyle,
      heroTitleLine1,
      heroTitleLine2,
      degrees,
      filteredDegrees,
      displayedDegrees,
      searchQuery,
      showAllDegrees,
      initialDisplayCount,
      scrollToDegrees,
      arrowRightMaroonIcon,
      catalogLoading,
      catalogError,
      catalogReady,
      loadCatalog,
      // Learning experience filter
      selectedLearningExperience,
      filterMenuOpen,
      learningExperienceOptions,
      // Sidebar
      showSidebar,
      selectedPathway,
      pathwayDataForSidebar,
      handleViewPathwayDetails,
      handlePathwayEnrolled,
      handleEnrollFromSidebar
    };
  },

  computed: {
    creditsNeeded() {
      const birthDate = this.learnerBirthDate;

      // If no data available, show the range
      if (!birthDate) {
        return '12-24';
      }

      // Calculate age
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      // If age is 22 or older, return 12 credits, otherwise 24
      return age >= 22 ? 12 : 24;
    }
  }
});
</script>

<style scoped>
.pathway-detail-page {
  font-family: Arial, Helvetica, sans-serif;
}

/* Hero Section */
.hero-section {
  min-height: 200px;
  height: 25vh;
  max-height: 280px;
  background-size: cover;
  background-position: center 25%;
}

.hero-overlay {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.7) 100%);
}

.hero-content {
  padding-bottom: 1.5rem;
}

.hero-title {
  font-size: 1.75rem;
  line-height: 1.1;
}

.hero-btn {
  background-color: #ffc627 !important;
  color: #191919 !important;
  font-weight: 700;
  text-transform: none;
  border-radius: 50px;
}

.school-link {
  color: #ffc627;
  text-decoration: underline;
}

/* Section Titles */
.section-title {
  font-size: 1.75rem;
  color: #191919;
}

.section-title-dark {
  font-size: 1.75rem;
  background-color: #333333;
}

/* Body Text */
.body-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #191919;
}

/* CTA Section */
.cta-section {
  background-color: #f1f1f1;
  padding: 3rem 1rem; /* py-space-lg px-space-xs */
}

@media (min-width: 960px) {
  .cta-section {
    padding: 6rem 0; /* py-lg-space-xxl px-lg-0 */
  }
}

.cta-card {
  background-color: white;
  padding: 1.5rem; /* p-space-sm */
}

@media (min-width: 960px) {
  .cta-card {
    padding: 4rem 4rem 3rem 4rem; /* pt-lg-space-xl px-lg-space-xl pb-lg-space-lg */
  }
}

.cta-title {
  font-size: 1.5rem; /* 24px mobile */
  color: #191919;
}

@media (min-width: 960px) {
  .cta-title {
    font-size: 2.25rem; /* 36px desktop h1-small */
  }
}

/* CTA Columns */
.cta-text-column {
  margin-bottom: 2rem; /* mb-space-md */
}

.cta-facts-column {
  margin-bottom: 2rem; /* mb-space-md */
}

@media (min-width: 960px) {
  .cta-text-column {
    margin-bottom: 0;
  }

  .cta-facts-column {
    margin-bottom: 0;
  }
}

/* Links */
.text-primary {
  color: #8c1d40 !important;
}

.text-decoration-underline {
  text-decoration: underline;
}

/* Quick Facts */
.quick-fact-label {
  font-weight: 700;
  font-size: 1rem;
  color: #191919;
  margin-bottom: 0;
}

.quick-fact-value {
  display: inline-block;
  font-size: 1.5rem; /* fs-xl = 1.5rem */
  font-weight: 700;
  color: #191919;
  background-color: #ffc627;
  padding: 0.5rem; /* p-space-xxs */
  margin-bottom: 0;
}

.quick-fact-detail {
  font-size: 0.875rem; /* fs-small / text-small */
  color: #747474; /* text-dark-1 */
  line-height: 1.5;
  margin-top: 1rem; /* mt-space-xs */
  margin-bottom: 2rem; /* mb-space-md */
  padding-bottom: 0.5rem; /* pb-space-xxs */
}

.quick-fact-detail-empty {
  margin-top: 1rem; /* mt-space-xs for visual consistency */
}

/* CTA Divider and Note */
.cta-divider {
  margin: 0;
  border: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.cta-note {
  margin-top: 2rem; /* mt-space-md */
  font-size: 1rem;
  line-height: 1.5;
  color: #191919;
}

/* Degrees Section */
.degrees-section {
  background-color: #fafafa; /* bg-light - slightly lighter than CTA's #f1f1f1 */
  padding: 3rem 1rem 2rem 1rem; /* pt-space-lg pb-space-md px-space-sm */
}

@media (min-width: 960px) {
  .degrees-section {
    padding: 6rem 0; /* py-lg-space-xxl px-lg-0 */
  }
}

.degrees-content-column {
  padding-right: 1rem;
}

@media (min-width: 960px) {
  .degrees-content-column {
    padding-right: 0;
  }
}

.degrees-title {
  font-size: 1.5rem;
  color: #191919;
  margin-bottom: 1.5rem; /* mb-space-sm */
}

@media (min-width: 960px) {
  .degrees-title {
    font-size: 2.25rem; /* h1-small desktop */
    margin-bottom: 2rem; /* mb-lg-space-md */
  }
}

.degrees-subtitle {
  font-size: 1rem;
  color: #191919;
  margin-bottom: 1.5rem; /* mb-space-sm */
}

@media (min-width: 960px) {
  .degrees-subtitle {
    margin-bottom: 2rem; /* mb-lg-space-md */
  }
}

.highlight-register {
  display: inline;
  background-color: #ffc627;
  font-weight: 700;
  padding: 0 0.25rem;
}

/* Search and Filter Row */
.search-filter-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
}

@media (max-width: 600px) {
  .search-filter-row {
    flex-direction: column;
    align-items: stretch;
  }
}

/* Search Box */
.degrees-search-wrapper {
  position: relative;
  flex: 1;
  background-color: #ffc627;
}

.degrees-search-input {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #d0d0d0;
  background-color: white;
  outline: none;
}

.degrees-search-input:focus {
  border-color: #191919;
}

.degrees-search-input::placeholder {
  color: #747474;
}

.degrees-search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #747474;
  pointer-events: none;
}

/* Learning Experience Filter */
.learning-experience-filter {
  flex-shrink: 0;
}

.filter-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: white;
  border: 1px solid #d0d0d0;
  border-radius: 0;
  font-size: 1rem;
  color: #191919;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
  white-space: nowrap;
}

.filter-button:hover,
.filter-button:focus {
  border-color: #191919;
  background-color: #fafafa;
}

.filter-button-active {
  border-color: #191919;
  background-color: white;
}

.filter-label {
  font-size: 1rem;
}

.filter-icon {
  color: #747474;
  transition: transform 0.2s ease;
}

.filter-icon-open {
  transform: rotate(180deg);
}

.filter-menu {
  min-width: 200px;
  margin-top: 4px;
}

.check-icon {
  color: #8c1d40;
}

/* Degree List */
.degrees-list-container {
  background-color: #f1f1f1;
  padding: 1rem 1rem 1.5rem 1rem; /* pt-space-xs pb-space-sm */
}

.degrees-list {
  margin: 0 0;
}

.degree-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafafa;
  padding: 1rem;
  margin-bottom: 1.5rem; /* mb-space-sm */
  text-decoration: none;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.degree-item:hover {
  background-color: #f5f5f5;
}

.degree-item:last-child {
  margin-bottom: 0;
}

.degree-item-title {
  font-size: 1.125rem; /* fs-large */
  font-weight: 700;
  color: #191919;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  padding-right: 1rem;
}

.degree-item-arrow {
  flex-shrink: 0;
  color: #191919;
}

.view-all-link {
  display: inline-block;
  margin-top: 0.5rem;
  margin-left: 1rem;
  color: #191919;
  text-decoration: underline;
  font-size: 1rem;
}

.view-all-link:hover {
  color: #8c1d40;
}

/* Loading, Error, Empty States */
.degrees-loading,
.degrees-error,
.degrees-empty {
  background-color: #fafafa;
  padding: 2rem;
  text-align: center;
}

.degrees-loading p,
.degrees-error p,
.degrees-empty p {
  margin: 0;
  font-size: 1rem;
  color: #747474;
}

.degrees-error p {
  color: #8c1d40;
}

/* Degrees Image */
.degrees-image {
  width: 100%;
  height: auto;
  object-fit: cover;
}

/* Empty State */
.empty-state {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
}

/* Responsive */
@media (min-width: 768px) {
  .hero-section {
    min-height: 300px;
    height: 35vh;
    max-height: 400px;
  }

  .hero-content {
    padding-bottom: 2rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }
}

@media (min-width: 960px) {
  .hero-section {
    min-height: 350px;
    height: 50vh;
    max-height: 500px;
  }

  .hero-content {
    padding-bottom: 1.5rem;
  }

  .hero-title {
    font-size: 3.75rem;
  }

  .section-title {
    font-size: 2.25rem;
  }

  .section-title-dark {
    font-size: 2.25rem;
  }
}
</style>
